// client/src/components/pages/LexiStar/Replay/trips/tripsDay.ts
//
// The Trips tab's day view (ST-00159), as pure functions: every trip of a day (or a date
// range) numbered and coloured, grouped by vehicle, switched on and off, drawn as GeoJSON,
// and kept in the address so a link reproduces the view. TripsView and its list and map
// layers are built on these; the tests pin them without a map.
//
// ST-00318: moved with the tab from the Geofences page (Geofencing/trips) to Replay.
import type { Feature, FeatureCollection, LineString, Point } from 'geojson';
import type {
  FleetStop,
  FleetTrip,
  FleetTripsResult,
  TripZoneRef,
} from '../../../../../types/clientPages/geofencing';
import { TRIPS_QUERY_KEYS } from '../replayPaths';

export type Basemap = 'street' | 'satellite';

// ─── Colour ──────────────────────────────────────────────────────────────────
// A categorical palette, one step per basemap: the light steps on the light Street map,
// the dark-surface steps on Satellite imagery (validated with the dataviz palette checks
// against #f8f4f0 and a dark imagery tone: CVD ΔE ≥ 8.4 and normal-vision ΔE ≥ 19 between
// neighbours). Map content does not follow the app theme, so these are literal colours.
// Trips are coloured in list order and the eight hues repeat, so colour is never the only
// cue: every trip carries its number (list row and the badge at its start) and a ring at
// its end, and hovering either side highlights the other.
export const TRIP_PALETTE: Record<Basemap, readonly string[]> = {
  street: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'],
  satellite: [
    '#3987e5',
    '#d95926',
    '#199e70',
    '#c98500',
    '#d55181',
    '#22a822',
    '#9085e9',
    '#e66767',
  ],
};

/** Trip number n (1-based) → its colour on that basemap. */
export const tripColor = (n: number, basemap: Basemap): string => {
  const palette = TRIP_PALETTE[basemap];
  return palette[(((n - 1) % palette.length) + palette.length) % palette.length] as string;
};

/** Casing under every line: dark on the light street map, light on dark imagery. */
export const TRIP_CASING: Record<Basemap, string> = {
  street: 'rgba(15, 23, 42, 0.55)',
  satellite: 'rgba(255, 255, 255, 0.9)',
};

/** Black or white text on a colour (WCAG relative luminance), for the number badges. */
export function inkOn(hex: string): '#ffffff' | '#0f172a' {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '#ffffff';
  const n = parseInt(m[1] as string, 16);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  // Contrast against white vs against #0f172a (L ≈ 0.009): pick the larger.
  return 1.05 / (L + 0.05) >= (L + 0.05) / 0.059 ? '#ffffff' : '#0f172a';
}

// ─── The day ─────────────────────────────────────────────────────────────────

/** A trip of the day view: the server's summary, its stable key and its number. */
export interface DayTrip extends FleetTrip {
  /** `<reg>~<start, epoch seconds base 36>`: stable across reloads, used in the address. */
  key: string;
  /** 1-based, in list order (vehicles by registration, then time). */
  n: number;
}

export interface DayVehicle {
  regNo: string;
  trips: DayTrip[];
  stops: FleetStop[];
  distanceKm: number;
  durationMin: number;
}

export interface TripsDay {
  vehicles: DayVehicle[];
  /** Every trip in list order. */
  trips: DayTrip[];
  byKey: Map<string, DayTrip>;
  totalKm: number;
  totalMin: number;
}

export const tripKey = (regNo: string, startTs: string): string =>
  `${regNo}~${Math.floor(Date.parse(startTs) / 1000).toString(36)}`;

/** The registration a trip key belongs to (keys split on their last `~`). */
export const regNoOfKey = (key: string): string => key.slice(0, Math.max(0, key.lastIndexOf('~')));

const byTime = <T extends { startTs: string }>(a: T, b: T) =>
  Date.parse(a.startTs) - Date.parse(b.startTs);

/** The server's answer → numbered trips grouped by vehicle (registration order). */
export function buildTripsDay(result: FleetTripsResult | null | undefined): TripsDay {
  const vehicles: DayVehicle[] = [];
  const trips: DayTrip[] = [];
  const byKey = new Map<string, DayTrip>();
  const sorted = [...(result?.vehicles ?? [])].sort((a, b) =>
    a.regNo.localeCompare(b.regNo, 'en', { numeric: true })
  );
  for (const v of sorted) {
    const dayTrips: DayTrip[] = [...v.trips].sort(byTime).map((t) => {
      const trip: DayTrip = { ...t, regNo: v.regNo, key: tripKey(v.regNo, t.startTs), n: 0 };
      trip.n = trips.push(trip);
      byKey.set(trip.key, trip);
      return trip;
    });
    vehicles.push({
      regNo: v.regNo,
      trips: dayTrips,
      stops: [...v.stops].sort(byTime),
      distanceKm: dayTrips.reduce((s, t) => s + (t.distanceKm || 0), 0),
      durationMin: dayTrips.reduce((s, t) => s + (t.durationMin || 0), 0),
    });
  }
  return {
    vehicles,
    trips,
    byKey,
    totalKm: trips.reduce((s, t) => s + (t.distanceKm || 0), 0),
    totalMin: trips.reduce((s, t) => s + (t.durationMin || 0), 0),
  };
}

// ─── Showing and hiding ──────────────────────────────────────────────────────

export interface TripVisibility {
  hiddenTrips: ReadonlySet<string>;
  hiddenVehicles: ReadonlySet<string>;
}

export const ALL_VISIBLE: TripVisibility = { hiddenTrips: new Set(), hiddenVehicles: new Set() };

export const isTripVisible = (trip: Pick<DayTrip, 'key' | 'regNo'>, vis: TripVisibility): boolean =>
  !vis.hiddenVehicles.has(trip.regNo) && !vis.hiddenTrips.has(trip.key);

/** A vehicle group's checkbox: every trip on, some, or none. */
export function vehicleVisibility(
  vehicle: DayVehicle,
  vis: TripVisibility
): 'all' | 'some' | 'none' {
  if (vis.hiddenVehicles.has(vehicle.regNo)) return 'none';
  const shown = vehicle.trips.filter((t) => !vis.hiddenTrips.has(t.key)).length;
  if (shown === vehicle.trips.length) return 'all';
  return shown === 0 ? 'none' : 'some';
}

/**
 * Switch one trip. Turning a trip on while its vehicle is off shows only that trip of the
 * vehicle (its other trips stay off).
 */
export function toggleTrip(
  vis: TripVisibility,
  trip: DayTrip,
  vehicle: DayVehicle
): TripVisibility {
  const hiddenTrips = new Set(vis.hiddenTrips);
  const hiddenVehicles = new Set(vis.hiddenVehicles);
  if (hiddenVehicles.has(vehicle.regNo)) {
    hiddenVehicles.delete(vehicle.regNo);
    vehicle.trips.forEach((t) =>
      t.key === trip.key ? hiddenTrips.delete(t.key) : hiddenTrips.add(t.key)
    );
  } else if (hiddenTrips.has(trip.key)) {
    hiddenTrips.delete(trip.key);
  } else {
    hiddenTrips.add(trip.key);
  }
  return { hiddenTrips, hiddenVehicles };
}

/** Switch a vehicle group: all on when any of it is off, else all off. */
export function toggleVehicle(vis: TripVisibility, vehicle: DayVehicle): TripVisibility {
  const hiddenTrips = new Set(vis.hiddenTrips);
  const hiddenVehicles = new Set(vis.hiddenVehicles);
  if (vehicleVisibility(vehicle, vis) === 'all') {
    hiddenVehicles.add(vehicle.regNo);
  } else {
    hiddenVehicles.delete(vehicle.regNo);
  }
  vehicle.trips.forEach((t) => hiddenTrips.delete(t.key));
  return { hiddenTrips, hiddenVehicles };
}

export const hideAll = (day: TripsDay): TripVisibility => ({
  hiddenTrips: new Set(),
  hiddenVehicles: new Set(day.vehicles.map((v) => v.regNo)),
});

// ─── Words ───────────────────────────────────────────────────────────────────

export const formatDuration = (min: number | null | undefined): string => {
  if (min == null) return '--';
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
};

/** A place: its zone's name when it is in one, else its coordinates. */
export const placeLabel = (
  zone: TripZoneRef | null | undefined,
  lat: number,
  lng: number
): string => zone?.name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;

export const fromTo = (trip: FleetTrip): string =>
  `${placeLabel(trip.startZone, trip.start.lat, trip.start.lng)} → ${placeLabel(trip.endZone, trip.end.lat, trip.end.lng)}`;

export const stopsLabel = (count: number): string =>
  count === 0 ? 'no stops' : `${count} stop${count === 1 ? '' : 's'}`;

// ─── Map data ────────────────────────────────────────────────────────────────

export interface TripLineProps {
  key: string;
  n: number;
  color: string;
}
export interface TripBadgeProps extends TripLineProps {
  /** The badge image (drawn on demand, see tripMapImages). */
  icon: string;
}
export interface DayStopProps {
  key: string;
  text: string;
}

export const BADGE_IMAGE_PREFIX = 'tripbadge:';
/** `tripbadge:<n>:<#colour>`: everything needed to draw the badge is in its name. */
export const badgeImageId = (n: number, color: string): string =>
  `${BADGE_IMAGE_PREFIX}${n}:${color}`;

export function tripLinesFC(
  trips: readonly DayTrip[],
  basemap: Basemap
): FeatureCollection<LineString, TripLineProps> {
  return {
    type: 'FeatureCollection',
    features: trips
      .filter((t) => t.path.length >= 2)
      .map((t) => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: t.path },
        properties: { key: t.key, n: t.n, color: tripColor(t.n, basemap) },
      })),
  };
}

export function tripStartsFC(
  trips: readonly DayTrip[],
  basemap: Basemap
): FeatureCollection<Point, TripBadgeProps> {
  return {
    type: 'FeatureCollection',
    features: trips.map((t) => {
      const color = tripColor(t.n, basemap);
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [t.start.lng, t.start.lat] },
        // A lower number is placed first, so it wins a collision.
        properties: { key: t.key, n: t.n, color, icon: badgeImageId(t.n, color) },
      };
    }),
  };
}

export function tripEndsFC(
  trips: readonly DayTrip[],
  basemap: Basemap
): FeatureCollection<Point, TripLineProps> {
  return {
    type: 'FeatureCollection',
    features: trips.map((t) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [t.end.lng, t.end.lat] },
      properties: { key: t.key, n: t.n, color: tripColor(t.n, basemap) },
    })),
  };
}

/** Stops as faint dots; `describe` builds the hover text. */
export function dayStopsFC(
  stops: readonly FleetStop[],
  describe: (stop: FleetStop) => string
): FeatureCollection<Point, DayStopProps> {
  return {
    type: 'FeatureCollection',
    features: stops
      .filter((s) => s.lat != null && s.lng != null)
      .map((s): Feature<Point, DayStopProps> => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
        properties: { key: `${s.regNo}~${s.startTs}`, text: describe(s) },
      })),
  };
}

/** Every coordinate the visible trips cover ([lng, lat]), for fitting the map. */
export const tripsCoords = (trips: readonly DayTrip[]): [number, number][] =>
  trips.flatMap((t) => (t.path.length ? t.path : [[t.start.lng, t.start.lat] as [number, number]]));

// ─── The address ─────────────────────────────────────────────────────────────

export interface TripsUrlState {
  /** Vehicles to load; empty = every vehicle. */
  vehicles: string[];
  /** YYYY-MM-DD, or null for today. */
  from: string | null;
  to: string | null;
  /** The trip selected: highlighted, and where Back from its replay lands (ST-00318). */
  trip: string | null;
  visibility: TripVisibility;
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const uniq = (values: string[]) => [...new Set(values.filter(Boolean))];

export function parseTripsParams(params: URLSearchParams): TripsUrlState {
  const from = params.get('from');
  const to = params.get('to');
  return {
    vehicles: uniq(params.getAll('vehicle')),
    from: from && DAY_RE.test(from) ? from : null,
    to: to && DAY_RE.test(to) ? to : null,
    trip: params.get('trip') || null,
    visibility: {
      hiddenTrips: new Set(uniq(params.getAll('hide'))),
      hiddenVehicles: new Set(uniq(params.getAll('hideVehicle'))),
    },
  };
}

/** `params` with the Trips tab's part replaced by `state` (other parameters kept). */
export function writeTripsParams(params: URLSearchParams, state: TripsUrlState): URLSearchParams {
  const next = new URLSearchParams(params);
  TRIPS_QUERY_KEYS.forEach((k) => next.delete(k));
  state.vehicles.forEach((v) => next.append('vehicle', v));
  if (state.from) next.set('from', state.from);
  if (state.to) next.set('to', state.to);
  if (state.trip) next.set('trip', state.trip);
  [...state.visibility.hiddenTrips].sort().forEach((k) => next.append('hide', k));
  [...state.visibility.hiddenVehicles].sort().forEach((v) => next.append('hideVehicle', v));
  return next;
}

/** YYYY-MM-DD → a local Date at midnight (what the date picker hands out), or null. */
export function dayToDate(day: string | null): Date | null {
  if (!day || !DAY_RE.test(day)) return null;
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}
